import React, { useState } from 'react';
import { CreditCard, CheckCircle, AlertCircle, Search, Clock } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import clientSelfServiceService from '../../../services/clientSelfServiceService';

const PaymentVerificationPanel = () => {
  const [formData, setFormData] = useState({
    companyEmail: '',
    transactionReference: '',
    amount: '',
    bankName: 'bsp'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationResult, setVerificationResult] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);

  const banks = [
    { value: 'bsp', label: 'Bank South Pacific (BSP)' },
    { value: 'kinabank', label: 'Kina Bank' },
    { value: 'creditbank', label: 'Credit Bank' }
  ];

  const handleVerifyPayment = async (e) => {
    e?.preventDefault();
    setError('');
    setVerificationResult(null);

    // Validation
    if (!formData?.companyEmail?.trim()) {
      setError('Please enter your company email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(formData?.companyEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!formData?.transactionReference?.trim()) {
      setError('Please enter the transaction reference number');
      return;
    }

    if (!formData?.amount || parseFloat(formData?.amount) <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    setLoading(true);
    const result = await clientSelfServiceService?.verifyPaymentTransaction({
      companyEmail: formData?.companyEmail,
      transactionReference: formData?.transactionReference,
      amount: formData?.amount,
      bankName: formData?.bankName
    });
    setLoading(false);

    setVerificationResult(result);

    // Load payment history if verification successful
    if (result?.success) {
      const history = await clientSelfServiceService?.getCompanyPaymentHistory(formData?.companyEmail);
      setPaymentHistory(history);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      matched: {
        bg: 'bg-emerald-100 dark:bg-emerald-900/30',
        text: 'text-emerald-700 dark:text-emerald-300',
        icon: CheckCircle
      },
      partial_match: {
        bg: 'bg-amber-100 dark:bg-amber-900/30',
        text: 'text-amber-700 dark:text-amber-300',
        icon: AlertCircle
      },
      not_found: {
        bg: 'bg-red-100 dark:bg-red-900/30',
        text: 'text-red-700 dark:text-red-300',
        icon: AlertCircle
      }
    };

    const config = statusConfig?.[status] || statusConfig?.not_found;
    const IconComponent = config?.icon;

    return (
      <div className={`${config?.bg} ${config?.text} px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm font-medium`}>
        <IconComponent size={16} />
        {status === 'matched' ? 'Payment Found' : status === 'partial_match' ? 'Partial Match' : 'Not Found'}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">Payment Verification</h4>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Verify your external bank transfer payment status. Enter your transaction details below to check if your payment has been received and is pending admin confirmation.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleVerifyPayment} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Company Email Address *
          </label>
          <Input
            type="email"
            value={formData?.companyEmail}
            onChange={(e) => setFormData({ ...formData, companyEmail: e?.target?.value })}
            placeholder="company@example.com"
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Bank Name *
          </label>
          <select
            value={formData?.bankName}
            onChange={(e) => setFormData({ ...formData, bankName: e?.target?.value })}
            className="flex h-10 w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 transition-all"
            disabled={loading}
          >
            {banks?.map(bank => (
              <option key={bank?.value} value={bank?.value}>
                {bank?.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Transaction Reference Number *
          </label>
          <Input
            type="text"
            value={formData?.transactionReference}
            onChange={(e) => setFormData({ ...formData, transactionReference: e?.target?.value })}
            placeholder="TXN123456789"
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Found on your bank transfer receipt or confirmation email
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Payment Amount (PNG Kina) *
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
              K
            </span>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData?.amount}
              onChange={(e) => setFormData({ ...formData, amount: e?.target?.value })}
              placeholder="0.00"
              className="pl-8"
              disabled={loading}
            />
          </div>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <AlertCircle size={16} className="text-red-600 dark:text-red-400" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        )}

        <Button
          type="submit"
          variant="default"
          className="w-full"
          disabled={loading}
          iconName="Search"
        >
          {loading ? 'Verifying...' : 'Verify Payment'}
        </Button>
      </form>

      {/* Verification Result */}
      {verificationResult && (
        <div className="border border-border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-foreground">Verification Result</h4>
            {getStatusBadge(verificationResult?.status)}
          </div>

          <div className="bg-muted/30 rounded-lg p-3">
            <p className="text-sm text-muted-foreground mb-2">{verificationResult?.message}</p>
            
            {verificationResult?.paymentDetails && (
              <div className="space-y-2 mt-3 pt-3 border-t border-border">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount:</span>
                  <span className="font-medium text-foreground">
                    K {parseFloat(verificationResult?.paymentDetails?.amount)?.toFixed(2)} {verificationResult?.paymentDetails?.currency}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Transaction Date:</span>
                  <span className="font-medium text-foreground">
                    {new Date(verificationResult?.paymentDetails?.transactionDate)?.toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="font-medium text-foreground capitalize">
                    {verificationResult?.paymentDetails?.status?.replace('_', ' ')}
                  </span>
                </div>
              </div>
            )}
          </div>

          {verificationResult?.status === 'matched' && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Clock size={16} className="text-emerald-600 dark:text-emerald-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-emerald-700 dark:text-emerald-300">
                    <strong>Next Steps:</strong> Your payment is pending admin verification. You will receive an email confirmation once your account is activated (typically within 24 hours).
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment History */}
      {paymentHistory?.length > 0 && (
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold text-foreground mb-3">Recent Payment History</h4>
          <div className="space-y-2">
            {paymentHistory?.map((payment, index) => (
              <div key={payment?.id || index} className="bg-muted/30 rounded-lg p-3 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <CreditCard size={16} className="text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">
                      K {parseFloat(payment?.amount)?.toFixed(2)} {payment?.currency}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {new Date(payment?.createdAt)?.toLocaleDateString()} • {payment?.paymentMethod?.toUpperCase()}
                  </p>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${
                  payment?.status === 'reconciled' ?'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' :'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                }`}>
                  {payment?.status === 'reconciled' ? 'Verified' : 'Pending'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <Icon name="AlertTriangle" size={16} className="text-amber-600 dark:text-amber-400 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs text-amber-700 dark:text-amber-300">
              <strong>Important:</strong> Bank transfers may take 24-48 hours to appear in our system. If your payment is not found, please wait and try again later. For urgent matters, contact support.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentVerificationPanel;